/**
 * AdminDashboard - Admin-only page to view all users and platform statistics
 */
import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faCrown, 
  faSearch,
  faSync,
  faUserCheck,
  faArrowUp,
  faImage,
  faCoins,
  faCalendarAlt,
  faChevronLeft,
  faChevronRight,
  faEye,
  faPlus,
  faGift,
  faUndo
} from '@fortawesome/free-solid-svg-icons';
import adminService from '../services/adminService';
import { billingService } from '../services/billingService';
import { useToast } from '../context/ToastContext';

const AdminDashboard = () => {
  const toast = useToast();
  
  // Global stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Users list
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(false);
  
  // Filters
  const [search, setSearch] = useState('');
  const [payingOnly, setPayingOnly] = useState(false);
  const [sortBy, setSortBy] = useState('signup_date');
  const [sortDesc, setSortDesc] = useState(true);
  
  // Selected user detail
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Fetch global stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await adminService.getGlobalStats();
      setStats(data);
    } catch (error) {
      console.error('[Admin] Failed to fetch stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setStatsLoading(false);
    }
  }, [toast]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const data = await adminService.listUsers({
        page,
        page_size: pageSize,
        search: search || undefined,
        paying_only: payingOnly || undefined,
        sort_by: sortBy,
        sort_desc: sortDesc,
      });
      setUsers(data.users);
      setTotalUsers(data.total_count);
      setHasMore(data.has_more);
    } catch (error) {
      console.error('[Admin] Failed to fetch users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setUsersLoading(false);
    }
  }, [page, pageSize, search, payingOnly, sortBy, sortDesc, toast]);

  // Fetch user detail
  const fetchUserDetail = useCallback(async (userId) => {
    setDetailLoading(true);
    try {
      const data = await adminService.getUserDetail(userId);
      setUserDetail(data);
    } catch (error) {
      console.error('[Admin] Failed to fetch user detail:', error);
      toast.error('Erreur lors du chargement du détail utilisateur');
    } finally {
      setDetailLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (selectedUser) {
      fetchUserDetail(selectedUser);
    } else {
      setUserDetail(null);
    }
  }, [selectedUser, fetchUserDetail]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [search, payingOnly, sortBy, sortDesc]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <FontAwesomeIcon icon={faCrown} className="text-yellow-500" />
          Admin Dashboard
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Vue d'ensemble de la plateforme et gestion des utilisateurs
        </p>
      </div>

      {/* Global Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={faUsers}
          label="Total Utilisateurs"
          value={stats?.total_users ?? '-'}
          loading={statsLoading}
          color="blue"
        />
        <StatCard
          icon={faCrown}
          label="Utilisateurs Payants"
          value={stats?.paying_users ?? '-'}
          subValue={stats ? `${Math.round((stats.paying_users / stats.total_users) * 100) || 0}%` : ''}
          loading={statsLoading}
          color="yellow"
        />
        <StatCard
          icon={faArrowUp}
          label="Total Upscales"
          value={stats?.total_upscales ?? '-'}
          subValue={stats ? `+${stats.upscales_7d} cette semaine` : ''}
          loading={statsLoading}
          color="green"
        />
        <StatCard
          icon={faImage}
          label="Total No Watermark"
          value={stats?.total_no_watermark ?? '-'}
          subValue={stats ? `+${stats.no_watermark_7d} cette semaine` : ''}
          loading={statsLoading}
          color="purple"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard
          icon={faUserCheck}
          label="Abonnés Actifs"
          value={stats?.active_subscribers ?? '-'}
          loading={statsLoading}
          color="indigo"
        />
        <StatCard
          icon={faCalendarAlt}
          label="Nouveaux (7j)"
          value={stats?.new_users_7d ?? '-'}
          loading={statsLoading}
          color="teal"
        />
        <StatCard
          icon={faCoins}
          label="Crédits Dépensés (Total)"
          value={stats?.total_credits_spent ?? '-'}
          loading={statsLoading}
          color="orange"
        />
      </div>

      {/* Users Table Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <FontAwesomeIcon icon={faUsers} />
            Liste des Utilisateurs ({totalUsers})
          </h2>
          
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher (email, nom, uid)..."
                className="pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
              />
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
            </form>
            
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                checked={payingOnly}
                onChange={(e) => setPayingOnly(e.target.checked)}
                className="rounded"
              />
              Payants uniquement
            </label>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="signup_date">Date d'inscription</option>
              <option value="last_login">Dernière connexion</option>
              <option value="total_upscale">Total upscales</option>
              <option value="credits_spent_total">Crédits dépensés</option>
            </select>
            
            <button
              onClick={() => setSortDesc(!sortDesc)}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              {sortDesc ? '↓ Desc' : '↑ Asc'}
            </button>
            
            <button
              onClick={() => { fetchStats(); fetchUsers(); }}
              className="p-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
              title="Rafraîchir"
            >
              <FontAwesomeIcon icon={faSync} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Utilisateur</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Status</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-400">Crédits</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-400">Upscales</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-400">No WM</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Inscription</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Dernière Co.</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {usersLoading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    <FontAwesomeIcon icon={faSync} className="animate-spin mr-2" />
                    Chargement...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    Aucun utilisateur trouvé
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr 
                    key={user.uid}
                    className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750"
                  >
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {user.display_name || 'Sans nom'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {user.email || user.uid}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {user.subscription_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          <FontAwesomeIcon icon={faCrown} className="text-yellow-500" />
                          Abonné
                        </span>
                      ) : user.is_paying ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          Payant
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                          Gratuit
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {user.credits_remaining}
                      </div>
                      <div className="text-xs text-gray-500">
                        {user.monthly_credits_remaining}M / {user.purchased_credits_remaining}P
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                      {user.total_upscale}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                      {user.total_no_watermark}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {formatDate(user.signup_date)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {formatDate(user.last_login)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setSelectedUser(user.uid)}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 
                          text-gray-600 dark:text-gray-400 hover:text-blue-500 transition-colors"
                        title="Voir détails"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} • {users.length} sur {totalUsers} utilisateurs
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal
          userId={selectedUser}
          userDetail={userDetail}
          loading={detailLoading}
          onClose={() => setSelectedUser(null)}
          onRefresh={() => { fetchUserDetail(selectedUser); fetchUsers(); }}
          formatDate={formatDate}
          toast={toast}
        />
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, subValue, loading, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400',
    teal: 'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <FontAwesomeIcon icon={icon} className="text-xl" />
        </div>
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400">{label}</div>
          {loading ? (
            <div className="h-7 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mt-1" />
          ) : (
            <>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
              </div>
              {subValue && (
                <div className="text-xs text-gray-500 dark:text-gray-400">{subValue}</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// User Detail Modal Component
const UserDetailModal = ({ userId, userDetail, loading, onClose, onRefresh, formatDate, toast }) => {
  const [creditAction, setCreditAction] = useState(null); // 'adjust' | 'bonus' | 'refund'
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditTxId, setCreditTxId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const resetForm = () => {
    setCreditAction(null);
    setCreditAmount('');
    setCreditReason('');
    setCreditTxId('');
  };

  const handleCreditAction = async () => {
    const amount = parseInt(creditAmount, 10);
    if (!amount || amount === 0) {
      toast.error('Montant invalide');
      return;
    }
    if (!creditReason.trim()) {
      toast.error('Raison requise');
      return;
    }

    // Validate before setting loading state
    if (creditAction === 'bonus' && amount <= 0) {
      toast.error('Le bonus doit être positif');
      return;
    }
    if (creditAction === 'refund') {
      if (amount <= 0) { toast.error('Le remboursement doit être positif'); return; }
      if (!creditTxId.trim()) { toast.error('ID de transaction requis'); return; }
    }

    setActionLoading(true);
    try {
      if (creditAction === 'adjust') {
        await billingService.adjustCredits(userId, amount, creditReason);
        toast.success(`Ajustement de ${amount > 0 ? '+' : ''}${amount} crédits appliqué`);
      } else if (creditAction === 'bonus') {
        await billingService.addBonusCredits(userId, amount, creditReason);
        toast.success(`${amount} crédits bonus ajoutés`);
      } else if (creditAction === 'refund') {
        await billingService.processRefund(userId, amount, creditTxId, creditReason);
        toast.success(`${amount} crédits remboursés`);
      }
      resetForm();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('[Admin] Credit action failed:', err);
      toast.error(`Erreur: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Détail Utilisateur
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FontAwesomeIcon icon={faSync} className="animate-spin text-2xl text-gray-400" />
            </div>
          ) : userDetail ? (
            <div className="space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InfoItem label="UID" value={userDetail.user?.uid} />
                <InfoItem label="Email" value={userDetail.user?.email || '-'} />
                <InfoItem label="Nom" value={userDetail.user?.display_name || '-'} />
                <InfoItem label="Inscription" value={formatDate(userDetail.user?.signup_date)} />
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InfoItem label="Crédits Restants" value={userDetail.user?.credits_remaining} />
                <InfoItem label="Crédits Mensuels" value={userDetail.user?.monthly_credits_remaining} />
                <InfoItem label="Crédits Achetés" value={userDetail.user?.purchased_credits_remaining} />
                <InfoItem label="Crédits Dépensés" value={userDetail.user?.credits_spent_total} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InfoItem label="Total Upscales" value={userDetail.user?.total_upscale} />
                <InfoItem label="Total No WM" value={userDetail.user?.total_no_watermark} />
                <InfoItem label="Abonné" value={userDetail.user?.subscription_active ? 'Oui' : 'Non'} />
                <InfoItem label="Plan" value={userDetail.user?.subscription_plan || '-'} />
              </div>

              {/* Credit Management Actions */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <FontAwesomeIcon icon={faCoins} className="text-yellow-500" />
                  Gestion des Crédits
                </h4>
                
                {/* Action buttons */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={() => { resetForm(); setCreditAction('adjust'); }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                      creditAction === 'adjust'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200'
                    }`}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Ajuster (+/-)
                  </button>
                  <button
                    onClick={() => { resetForm(); setCreditAction('bonus'); }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                      creditAction === 'bonus'
                        ? 'bg-green-600 text-white'
                        : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200'
                    }`}
                  >
                    <FontAwesomeIcon icon={faGift} />
                    Bonus
                  </button>
                  <button
                    onClick={() => { resetForm(); setCreditAction('refund'); }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                      creditAction === 'refund'
                        ? 'bg-orange-600 text-white'
                        : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 hover:bg-orange-200'
                    }`}
                  >
                    <FontAwesomeIcon icon={faUndo} />
                    Remboursement
                  </button>
                </div>

                {/* Action form */}
                {creditAction && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {creditAction === 'adjust' && 'Ajustement de crédits (valeur positive ou négative)'}
                      {creditAction === 'bonus' && 'Ajouter des crédits bonus (valeur positive)'}
                      {creditAction === 'refund' && 'Rembourser des crédits (valeur positive)'}
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="number"
                        value={creditAmount}
                        onChange={(e) => setCreditAmount(e.target.value)}
                        placeholder={creditAction === 'adjust' ? 'Montant (+/-)' : 'Montant'}
                        className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                          bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                      <input
                        type="text"
                        value={creditReason}
                        onChange={(e) => setCreditReason(e.target.value)}
                        placeholder="Raison..."
                        className="flex-[2] px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                          bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>

                    {creditAction === 'refund' && (
                      <input
                        type="text"
                        value={creditTxId}
                        onChange={(e) => setCreditTxId(e.target.value)}
                        placeholder="ID de transaction originale..."
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                          bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    )}

                    <div className="flex gap-2">
                      <button
                        onClick={handleCreditAction}
                        disabled={actionLoading}
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium
                          hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {actionLoading && <FontAwesomeIcon icon={faSync} className="animate-spin" />}
                        Confirmer
                      </button>
                      <button
                        onClick={resetForm}
                        className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                          text-gray-700 dark:text-gray-300 text-sm hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                  Transactions Récentes ({userDetail.recent_transactions?.length || 0})
                </h4>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {userDetail.recent_transactions?.length > 0 ? (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-gray-500">
                          <th className="text-left pb-2">Type</th>
                          <th className="text-right pb-2">Montant</th>
                          <th className="text-left pb-2">Description</th>
                          <th className="text-left pb-2">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userDetail.recent_transactions.slice(0, 20).map((tx) => (
                          <tr key={tx.id} className="border-t border-gray-200 dark:border-gray-600">
                            <td className="py-1 text-gray-700 dark:text-gray-300">{tx.transaction_type}</td>
                            <td className={`py-1 text-right font-medium ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {tx.amount >= 0 ? '+' : ''}{tx.amount}
                            </td>
                            <td className="py-1 text-gray-600 dark:text-gray-400 truncate max-w-[200px]">
                              {tx.description}
                            </td>
                            <td className="py-1 text-gray-500">{formatDate(tx.timestamp)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-center text-gray-500 py-4">Aucune transaction</div>
                  )}
                </div>
              </div>

              {/* Recent Jobs */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                  Jobs Récents ({userDetail.recent_jobs?.length || 0})
                </h4>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {userDetail.recent_jobs?.length > 0 ? (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-gray-500">
                          <th className="text-left pb-2">Job ID</th>
                          <th className="text-left pb-2">Status</th>
                          <th className="text-left pb-2">Type</th>
                          <th className="text-left pb-2">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userDetail.recent_jobs.slice(0, 10).map((job) => (
                          <tr key={job.id} className="border-t border-gray-200 dark:border-gray-600">
                            <td className="py-1 text-gray-700 dark:text-gray-300 font-mono text-xs">
                              {job.job_id?.slice(0, 12)}...
                            </td>
                            <td className="py-1">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                job.status === 'completed' ? 'bg-green-100 text-green-700' :
                                job.status === 'failed' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {job.status}
                              </span>
                            </td>
                            <td className="py-1 text-gray-600 dark:text-gray-400">{job.job_type || 'upscale'}</td>
                            <td className="py-1 text-gray-500">{formatDate(job.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-center text-gray-500 py-4">Aucun job</div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12">Erreur de chargement</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Info Item Component
const InfoItem = ({ label, value }) => (
  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
    <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
    <div className="font-medium text-gray-900 dark:text-white mt-1 truncate">
      {value ?? '-'}
    </div>
  </div>
);

export default AdminDashboard;
